/** LocalStorageCache acts like a Map except values are persisted to localStorage
 * and invalidated after a given timeout.
 *
 * const cache = new LocalStorageCache<string>({ 
 *   namespace: "my_cache",
 *   timeoutSeconds: 600 
 * })
 * cache.set("key", "my value")
 * cache.get("key") // "my value"
 * // Wait 10 minutes
 * cache.get("key") // undefined
 */

interface CacheEntry<T> {
  timestamp: number
  value: T
}

interface CacheStorage<T> {
  [key: string]: CacheEntry<T>
}

export class LocalStorageCache<T = any> {
  private namespace: string
  private timeoutMs: number

  constructor({ 
    namespace, 
    timeoutSeconds 
  }: { 
    namespace: string
    timeoutSeconds: number 
  }) {
    this.namespace = namespace
    this.timeoutMs = timeoutSeconds * 1000
  }

  private getStorageKey(): string {
    return `cache:${this.namespace}`
  }

  private getStorage(): CacheStorage<T> {
    try {
      const stored = localStorage.getItem(this.getStorageKey())
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.warn(`Failed to read from localStorage namespace "${this.namespace}":`, error)
      return {}
    }
  }

  private setStorage(storage: CacheStorage<T>): void {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(storage))
    } catch (error) {
      console.warn(`Failed to write to localStorage namespace "${this.namespace}":`, error)
      // Storage full or unavailable - silently fail
    }
  }

  set(key: string, value: T): void {
    const storage = this.getStorage()
    storage[key] = {
      timestamp: Date.now(),
      value
    }
    this.setStorage(storage)
  }

  get(key: string): T | undefined {
    const storage = this.getStorage()
    const cached = storage[key]

    if (!cached) {
      return undefined
    }

    const isExpired = Date.now() - cached.timestamp > this.timeoutMs
    if (isExpired) {
      // Clean up expired entry
      delete storage[key]
      this.setStorage(storage)
      return undefined
    }

    return cached.value
  }

  /** Remove a specific key from the cache */
  delete(key: string): void {
    const storage = this.getStorage()
    delete storage[key]
    this.setStorage(storage)
  }

  /** Clear all entries from the cache */
  clear(): void {
    try {
      localStorage.removeItem(this.getStorageKey())
    } catch (error) {
      console.warn(`Failed to clear localStorage namespace "${this.namespace}":`, error)
    }
  }

  /** Check if a key exists and is not expired */
  has(key: string): boolean {
    return this.get(key) !== undefined
  }

  /** Get all non-expired keys in the cache */
  keys(): string[] {
    const storage = this.getStorage()
    const now = Date.now()
    
    return Object.keys(storage).filter(key => {
      const cached = storage[key]
      return now - cached.timestamp <= this.timeoutMs
    })
  }
}